// MARK://dhk8/pos/stores/theme
// Manual light/dark toggle for the POS shell — NOT prefers-color-scheme.
//
// Lane #174 — el tema ahora sigue al usuario (per-User en backend), no a la
// terminal. localStorage queda como cache local + fallback para boot rápido y
// "modo invitado" (antes del login). Al loguearse, hidratamos desde
// `GET /v1/auth/me/preferences`; al togglear, persistimos con
// `PATCH /v1/auth/me/preferences` (fire-and-forget — no bloquea UI).
//
// Aplicando el tema escribimos AMBOS:
//   - <html data-theme="dark|light">  → drives the `--pos-*` token blocks in
//     styles/tokens.css.
//   - the matching `dark`/`light` class on <html> → drives the legacy
//     `@dockethub/ui` theme-nintaigo layer (`[data-brand="nintaigo"].dark`) so the
//     not-yet-migrated screens flip too.
//
// Migra `'black'` legacy → `'dark'`.
import { create } from 'zustand'
import { persist } from 'zustand/middleware'

import { fetchMyPreferences, patchMyPreferences } from '../lib/clients/preferences.js'

export type ColorTheme = 'light' | 'dark'

const STORAGE_KEY = 'dhk8-pos-theme'

function applyToHtml(theme: ColorTheme): void {
  if (typeof document === 'undefined') return
  const html = document.documentElement
  html.classList.remove('light', 'dark', 'hig', 'black')
  html.classList.add(theme)
  html.dataset['theme'] = theme
  html.style.colorScheme = theme
}

function migrateTheme(raw: string | null): ColorTheme {
  if (raw === 'light') return 'light'
  // 'dark', 'black', null, unknown → 'dark'
  return 'dark'
}

interface ThemeState {
  theme: ColorTheme
  /** True una vez que `hydrateFromBackend()` resolvió (o falló silenciosamente). */
  hydrated: boolean
  setTheme: (t: ColorTheme) => void
  toggleTheme: () => void
  /** Llamar después del login (auth-store). Mergea el theme remoto sobre el local. */
  hydrateFromBackend: () => Promise<void>
}

export const useThemeStore = create<ThemeState>()(
  persist(
    (set, get) => ({
      theme: 'dark',
      hydrated: false,
      setTheme: (theme) => {
        set({ theme })
        applyToHtml(theme)
        // Fire-and-forget — si falla (offline, sin sesión) seguimos con local.
        void patchMyPreferences({ theme }).catch(() => undefined)
      },
      toggleTheme: () => {
        const next: ColorTheme = get().theme === 'dark' ? 'light' : 'dark'
        set({ theme: next })
        applyToHtml(next)
        void patchMyPreferences({ theme: next }).catch(() => undefined)
      },
      hydrateFromBackend: async () => {
        try {
          const prefs = await fetchMyPreferences()
          // Si el backend tiene preferencia explícita y difiere del local, gana
          // el backend (el setting "sigue al login"). Si el user nunca seteó
          // tema en el backend, hacemos upstream del local actual para que
          // futuros logins en otras terminales lo respeten.
          if (prefs?.theme === 'light' || prefs?.theme === 'dark') {
            if (prefs.theme !== get().theme) {
              set({ theme: prefs.theme })
              applyToHtml(prefs.theme)
            }
          } else {
            void patchMyPreferences({ theme: get().theme }).catch(() => undefined)
          }
        } catch {
          // sin red / sin auth → quedamos con local
        } finally {
          set({ hydrated: true })
        }
      },
    }),
    {
      name: STORAGE_KEY,
      partialize: (state) => ({ theme: state.theme }),
      storage: {
        getItem: () => {
          if (typeof localStorage === 'undefined') return { state: { theme: 'dark' as ColorTheme }, version: 0 }
          return { state: { theme: migrateTheme(localStorage.getItem(STORAGE_KEY)) }, version: 0 }
        },
        setItem: (_name, value) => {
          if (typeof localStorage === 'undefined') return
          const v = value as { state: { theme: ColorTheme } }
          localStorage.setItem(STORAGE_KEY, v.state.theme)
        },
        removeItem: () => {
          if (typeof localStorage === 'undefined') return
          localStorage.removeItem(STORAGE_KEY)
        },
      },
      onRehydrateStorage: () => (state) => {
        applyToHtml(state?.theme ?? 'dark')
      },
    }
  )
)

/** Call once at boot (main.tsx) so the `<html>` attrs match the persisted
 *  theme before first paint of the shell. */
export function initTheme(): void {
  applyToHtml(useThemeStore.getState().theme)
}
